import { Grid, Box } from '@mui/material';
import { BookOpen, Users, ClipboardCheck, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WelcomeBanner } from '@/components/dashboard/WelcomeBanner';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { useAuth } from '@/hooks/useAuth';
import { useTeacherDashboard } from '@/hooks/useTeacherDashboard';
import { Loader } from '@/components/feedback/Loader';
import { TeacherDashboardCard } from '@/features/teachers/components/TeacherDashboardCard';
import useLeaveStore from '@/store/leaveStore';
import { useEffect } from 'react';

export function TeacherDashboard() {
  const { user } = useAuth();
  const { dashboard, loading } = useTeacherDashboard();
  const navigate = useNavigate();

  const { summary: leaveSummary, fetchSummary } = useLeaveStore();

  useEffect(() => {
    fetchSummary('teacher', user?.id);
  }, [user?.id]);

  if (loading || !dashboard) return <Loader />;

  return (
    <Box>
      <WelcomeBanner
        title={`Welcome back, ${user?.name}`}
        message={`Here's your class schedule and tasks for today, ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}.`}
        actionLabel="View Timetable"
        onAction={() => {}}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherDashboardCard
            title="Today's Classes"
            value={dashboard.stats.totalClasses}
            icon={BookOpen}
            color="primary"
            onClick={() => navigate('/my-classes')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherDashboardCard
            title="My Students"
            value={dashboard.stats.totalStudents}
            subtitle={`Class Teacher: ${dashboard.stats.classTeacherOf}`}
            icon={Users}
            color="info"
            onClick={() => navigate('/my-students')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherDashboardCard
            title="Attendance"
            value={`${Math.round((dashboard.attendanceSummary.present / dashboard.attendanceSummary.total) * 100 || 0)}%`}
            subtitle="Overall for your classes"
            icon={ClipboardCheck}
            color="success"
            onClick={() => navigate('/teacher-attendance')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherDashboardCard
            title="Pending Homework"
            value={dashboard.homeworkSummary.pending}
            subtitle={`${dashboard.homeworkSummary.reviewed} reviewed`}
            icon={FileText}
            color="warning"
            onClick={() => navigate('/homework')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <WidgetContainer title="Today's Schedule">
            {dashboard.todaysClasses.length > 0 ? (
              <Box>
                {dashboard.todaysClasses.map((cls, idx) => (
                  <Box 
                    key={idx}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box>
                      <Box fontWeight={600} mb={0.5}>Period {cls.period}: {cls.subject}</Box>
                      <Box color="text.secondary" fontSize="0.875rem">{cls.className} - {cls.sectionName}</Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box fontWeight={500} color="primary.main">{cls.time}</Box>
                      <Box color="text.secondary" fontSize="0.875rem">{cls.room}</Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No classes scheduled for today.
              </Box>
            )}
          </WidgetContainer>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <WidgetContainer title="Quick Actions">
            {[
              { label: 'Mark Attendance', path: '/teacher-attendance' },
              { label: 'Assign Homework', path: '/homework' },
              { label: 'Update Classwork Diary', path: '/classwork' },
              { label: 'Apply Leave', path: '/leaves/my' }
            ].map((action, idx) => (
              <Box 
                key={idx}
                onClick={() => navigate(action.path)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, background-color 0.2s',
                  '&:hover': { bgcolor: 'action.hover', transform: 'translateX(4px)' }
                }}
              >
                {action.label}
              </Box>
            ))}
          </WidgetContainer>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <WidgetContainer title="Leave Summary">
            {leaveSummary ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box color="text.secondary">Pending</Box>
                  <Box fontWeight={500} color="warning.main">{leaveSummary.pending}</Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box color="text.secondary">Approved</Box>
                  <Box fontWeight={500} color="success.main">{leaveSummary.approved}</Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box color="text.secondary">Rejected</Box>
                  <Box fontWeight={500} color="error.main">{leaveSummary.rejected}</Box>
                </Box>
                <Box 
                  onClick={() => navigate('/leaves')}
                  sx={{ 
                    mt: 2, 
                    color: 'primary.main', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View All Leave Requests
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                Loading...
              </Box>
            )}
          </WidgetContainer>
        </Grid>
      </Grid>
    </Box>
  );
}
